import PropTypes from "prop-types";
import {cn} from "@/utils/cn";
import useSubscription from "@/hooks/useSubscription";
import { formatMoney } from "@/utils/formatMoney";
import {observer} from "mobx-react";
import {useEffect, createRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {product as productStore} from "@/store/Product";
import {price as priceStore} from "@/store/Price";
import {triggerEvent} from "@/utils/custom-events";
import AddToCart from "@/components/AddToCart";
import {CSSTransition, SwitchTransition} from "react-transition-group";
import QuantitySelector from "@/components/QuantitySelector";
import {translateRecharge} from "@/utils/translate-recharge";

const ProductForm = observer(
  ({
    product,
    benefitsSubscriptionHTML,
    benefitsOneTimeHTML,
    measure,
    isBundle,
    isEbook,
    isGiftCard,
    quantityAmount,
    otpTags,
    subTags,
  }) => {
    const {t} = useTranslation();
    const bottomLayerRef = createRef(null);
    const isPrimalHarvestDeFr = window.location.hostname === 'primalharvest.de' || window.location.hostname === 'primalharvest.at' || window.location.hostname === 'primalharvest.ch' || window.location.hostname === 'primalharvest.fr';

    useEffect(() => {
      if (!isPrimalHarvestDeFr) {
        setSubscriptionActive(false);
      }
    }, []);

    const price = priceStore.currentPrice?.price;
    const subscriptionPrice = priceStore?.pricesQtd1?.subscriptionPrice;
    const otpPrice = priceStore?.pricesQtd1?.otpPrice;

    const {
      hasSubscription,
      isActive,
      sellingPlans,
      sellingPlanId,
      getSellingPlan,
      setSellingPlanId,
      setSubscriptionActive,
    } = useSubscription(product, productStore.sellingPlanId);

    useEffect(() => {
      if (productStore.sellingPlanId !== sellingPlanId) {
        productStore.setSellingPlan(sellingPlanId);
      }
    }, [sellingPlanId]);

    // initialize URL Params
    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const qtd = params.get("quantity");
      const buybox = params.get("buybox");
      if (buybox && buybox === "otp") {
        setSubscriptionActive(false);
      }
      if (qtd) {
        productStore.setQuantity(Number(qtd));
      }
    }, [setSubscriptionActive]);

    const sellingPlanOptions = getSellingPlan(sellingPlanId)?.options;
    const sellingPlanLabel = translateRecharge(
      (sellingPlanOptions &&
        (sellingPlanOptions[1]
          ? sellingPlanOptions[1].value
          : sellingPlanOptions[0].value)) ??
        "",
    );

    const quantities = createNumericArrayFromString(quantityAmount);
    const addToCartLabel = `${t("action.add_to_cart")} • ${formatMoney(price)} ${hasSubscription ? (isActive ? `/ ${sellingPlanLabel}` : "") : ""}`;

    const handleSubscriptionChange = isActive => {
      setSubscriptionActive(isActive);
      productStore.setQuantity(1);
    };

    const onAddToCart = () => {
      // Using native JS API to tell others the cart has been updated.
      // This is used by the upsell feature and custom datalayer impl.
      triggerEvent("cart:added", {
        productId: product.id,
        productName: product.title,
        variantId: productStore.variantId,
        quantity: productStore.quantity,
        frequencyId: productStore.sellingPlanId,
        subtotal: product.price,
      });
    };

    const changeSlide = position => {
      triggerEvent("slide:update", {
        index: position,
      });
    };

    return (
      <div className="flex flex-col">
        <input
          className="sr-only"
          type="radio"
          name="id"
          value={productStore.variantId}
          readOnly
          aria-label="Variant ID"
        />

        {isEbook ? (
          <div
            className={
              "mt-[-1px] flex flex-col gap-4 overflow-hidden rounded-2xl bg-white px-4 py-6 transition-all md:gap-6 md:rounded-3xl md:px-6 md:pb-8 md:pt-6"
            }
          >
            <AddToCart label={addToCartLabel} onAddToCart={onAddToCart} />
            <div
              className="[&_.flex-1:nth-child(n+2)]:hidden"
              dangerouslySetInnerHTML={{__html: benefitsOneTimeHTML}}
            ></div>
          </div>
        ) : isGiftCard ? (
          <div
            className={
              "mt-[-1px] flex flex-col gap-4 overflow-hidden rounded-2xl bg-white px-4 py-6 transition-all md:gap-6 md:rounded-3xl md:px-6 md:pb-8 md:pt-6"
            }
          >
            <div className="flex flex-wrap gap-2 2xl:flex-nowrap max-sm:[&_label]:basis-[calc(50%-0.5rem)]">
              {product.variants.map((variant, i) => (
                <QuantityRadio
                  key={variant.id}
                  label={`${variant.title}`}
                  name={product.handle}
                  isActive={productStore.variantId === variant.id}
                  onValueChange={() => {
                    productStore.setVariantId(variant.id);
                    changeSlide(i + 1);
                  }}
                />
              ))}
            </div>

            <AddToCart label={addToCartLabel} onAddToCart={onAddToCart} />

            <div
              className="[&_.flex-1:nth-child(n+2)]:hidden"
              dangerouslySetInnerHTML={{__html: benefitsOneTimeHTML}}
            ></div>
          </div>
        ) : hasSubscription ? (
          <SwitchTransition>
            <CSSTransition
              in={true}
              nodeRef={bottomLayerRef}
              timeout={150}
              classNames="fade"
              key={isActive}
            >
              <div ref={bottomLayerRef}>
                {isPrimalHarvestDeFr && (
                  <div className="top-layer flex">
                    <SubscriptionRadio
                      isActive={isActive}
                      handleSubscriptionChange={() =>
                        handleSubscriptionChange(true)
                      }
                      name={product.handle}
                      tag={t("product.subscription_tag")}
                      label={t("product.subscription")}
                      theme="green"
                      price={subscriptionPrice?.price}
                      compareAtPrice={subscriptionPrice?.compareAtPrice}
                    />
                    <SubscriptionRadio
                      isActive={!isActive}
                      handleSubscriptionChange={() =>
                        handleSubscriptionChange(false)
                      }
                      name={product.handle}
                      tag={t("product.otp_tag")}
                      label={t("product.otp")}
                      theme="white"
                      price={otpPrice?.price}
                      compareAtPrice={otpPrice?.compareAtPrice}
                    />
                  </div>
                )}
                <div
                  className={cn(
                    "bottom-layer mt-[-1px] flex flex-col gap-4 overflow-hidden rounded-b-2xl bg-white px-4 pb-4 pt-5 transition-all md:gap-6 md:rounded-b-3xl md:px-4 md:py-6",
                    isActive
                      ? "rounded-tr-2xl md:rounded-tr-3xl"
                      : "rounded-tl-2xl md:rounded-tl-3xl",
                    !isPrimalHarvestDeFr && "rounded-t-2xl md:rounded-t-3xl"
                  )}
                >
                  <input
                    aria-label={`${product.title} quantity`}
                    className="sr-only"
                    id={`quantity-${product.id}`}
                    type="number"
                    value={productStore.quantity}
                    readOnly
                  />
                  <div className="flex flex-col gap-2 md:gap-6">
                    <div
                      className={cn(
                        `grid justify-center gap-2`,
                        isActive
                          ? sellingPlans.length === 3
                            ? "grid-cols-3"
                            : "grid-cols-2"
                          : "grid-cols-2 md:grid-cols-4",
                      )}
                    >
                      {isActive &&
                        product.sellingPlanGroups.map((group, i) => {
                          return group.selling_plans.map((selling_plan, j) => {  
                            const tag = subTags.find(({index}) => i + 1 === index)?.tag ?? "";
                            return (
                              <SellingPlanRadio
                                key={selling_plan.id}
                                tag={tag}
                                group={group}
                                selling_plan={selling_plan}
                                name={product.handle}
                                isActive={sellingPlanId === selling_plan.id}
                                onValueChange={sellingPlanId => {
                                  setSellingPlanId(sellingPlanId);
                                  productStore.setSellingPlan(sellingPlanId);
                                }}
                              />
                            );
                          });
                        })}

                      {!isActive && !isBundle && (
                        <>
                          {quantities.map((qtd, i) => {
                            const tag =
                              otpTags?.find(({index}) => i + 1 === index)
                                ?.tag ?? "";
                            const unitTypeLabel = getUnitypeLabel(qtd, measure, t);
                            const prices = product.prices.find(
                              price => price.quantity === qtd,
                            );
                            const discountPercentage = prices?.price
                              ? Math.round(
                                  (1 - prices.price / prices.compareAtPrice) *
                                    100,
                                )
                              : 0;

                            return (
                              <QuantityRadio
                                key={qtd}
                                tag={tag}
                                label={unitTypeLabel}
                                name={product.handle}
                                isActive={productStore.quantity === qtd}
                                discountPercentage={discountPercentage}
                                onValueChange={() => {
                                  productStore.setQuantity(qtd);
                                  changeSlide(0);
                                }}
                              />
                            );
                          })}
                        </>
                      )}
                    </div>

                    <AddToCart
                      label={addToCartLabel}
                      onAddToCart={onAddToCart}
                    />
                  </div>

                  {isActive
                    ? benefitsSubscriptionHTML?.trim() && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: benefitsSubscriptionHTML,
                          }}
                        ></div>
                      )
                    : benefitsOneTimeHTML?.trim() && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: benefitsOneTimeHTML,
                          }}
                        ></div>
                      )}
                </div>
              </div>
            </CSSTransition>
          </SwitchTransition>
        ) : (
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 rounded-2xl bg-white px-4 py-6 pb-1.5 lg:pb-0 md:gap-6 md:rounded-3xl md:px-6">
            <QuantitySelector
              className="w-full lg:h-[56px]"
              value={productStore.quantity}
              disablePlus={productStore.quantity >= 4}
              disableMinus={productStore.quantity === 1}
              onValueChange={quantity => {
                productStore.setQuantity(quantity);
                changeSlide(0);
              }}
            />
            <AddToCart label={addToCartLabel} onAddToCart={onAddToCart} />
            <div
              className="[&_.flex-1:nth-child(n+2)]:hidden"
              dangerouslySetInnerHTML={{__html: benefitsOneTimeHTML}}
            ></div>
          </div>
        )}
      </div>
    );
  },
);

ProductForm.propTypes = {
  product: PropTypes.object.isRequired,
  benefitsSubscriptionHTML: PropTypes.string,
  benefitsOneTimeHTML: PropTypes.string,
  defaultVariantId: PropTypes.number,
  measure: PropTypes.string,
};

function createNumericArrayFromString(inputStr) {
  if (!inputStr || typeof inputStr !== 'string') return [1];
  const uniqueNums = [
    ...new Set(
      inputStr
        .split(',')
        .map(s => s.trim())
        .map(Number)
        .filter(n => !isNaN(n) && [2, 3, 4, 6].includes(n))
    )
  ];
  return [1, ...uniqueNums];
}

const SubscriptionRadio = ({
  isActive,
  handleSubscriptionChange,
  name,
  tag,
  label,
  price,
  compareAtPrice,
  theme = "green",
}) => {
  return (
    <label
      className={cn(
        "flex-1 cursor-pointer justify-end transition-all",
        theme === "green" &&
          "flex flex-col items-center rounded-t-2xl text-dark-green md:rounded-t-3xl",
        theme === "white" &&
          "flex flex-col items-center rounded-t-2xl text-dark-green md:rounded-t-3xl",
        isActive && theme === "white" ? "" : "bg-transparent",
        !isActive && "lg:hover:opacity-70",
      )}
    >
      {
        <div
          className={cn(
            "flex min-h-[24px] w-full justify-center md:min-h-[20px]",
          )}
        >
          <div
            className={cn(
              "flex items-center justify-center px-4 py-1 text-center",
              theme === "green"
                ? "bg-light-green text-deep-green"
                : theme === "white" && "bg-gray text-white",
              isActive
                ? "w-full rounded-t-3xl"
                : "w-fit rounded-2xl md:rounded-3xl",
            )}
          >
            <span className="text-[0.875rem] font-semibold not-italic leading-[120%] md:font-semibold">
              {tag}
            </span>
          </div>
        </div>
      }

      <div
        className={cn(
          "w-full",
          isActive && theme === "green"
            ? "bg-white text-dark-green"
            : !isActive && theme === "green"
              ? "text-dark-green"
              : "",
          isActive &&
            theme === "white" &&
            "overflow-hidden rounded-t-2xl bg-white md:rounded-t-3xl",
        )}
      >
        <input
          type="radio"
          className="sr-only"
          name={`${name}-subscription`}
          checked={isActive}
          onChange={handleSubscriptionChange}
          aria-label="subscription"
        />

        <div className="flex min-h-[60px] flex-col items-center justify-center p-4 text-deep-green md:flex-row md:justify-between md:gap-2 md:px-6 md:py-2">
          <div className="flex flex-col items-center justify-center gap-1 md:flex-row md:gap-2">
            <svg
              className="hidden lg:flex"
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 26 24"
              fill="none"
            >
              <circle
                cx="12.5"
                cy="12"
                r="11.5"
                className={
                  theme === "green" && isActive
                    ? "stroke-deep-green"
                    : theme === "green" && !isActive
                      ? "stroke-deep-green"
                      : theme === "white" && "stroke-deep-green"
                }
              />
              <circle
                cx="12.5"
                cy="12"
                r="7"
                className={cn(
                  isActive && theme === "green"
                    ? "fill-deep-green"
                    : isActive && theme === "white"
                      ? "fill-deep-green"
                      : "fill-transparent",
                  "transition-colors",
                )}
              />
            </svg>
            <span className="body--regular">{label}</span>
          </div>
          <div className="flex items-center gap-2 md:flex-col md:gap-0">
            <span className="body--regular font-semibold leading-[120%]">
              {formatMoney(price)}
            </span>
            {compareAtPrice && (
              <span className="body--regular text-end font-semibold line-through decoration-1 opacity-50">
                {formatMoney(compareAtPrice)}
              </span>
            )}
          </div>
        </div>
      </div>
    </label>
  );
};

SubscriptionRadio.propTypes = {
  name: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  handleSubscriptionChange: PropTypes.func,
  tag: PropTypes.string,
  label: PropTypes.string.isRequired,
  price: PropTypes.any,
  compareAtPrice: PropTypes.any,
  theme: PropTypes.string,
};

const SellingPlanRadio = ({
  selling_plan,
  hideDynamicLabel,
  name,
  tag,
  isActive,
  onValueChange,
}) => {
  const {t} = useTranslation();
  return (
    <label
      className={cn(
        "button relative flex flex-grow cursor-pointer flex-col justify-center rounded-[1.5rem] p-4 md:rounded-[6.25rem] md:px-4 md:py-2",
        isActive &&
          "button--quaternary border-deep-green bg-deep-green hover:bg-deep-green hover:text-white",
        !isActive && "button--secondary lg:hover:opacity-70",
      )}
    >
      <Tag label={tag} />
      <input
        type="radio"
        className="sr-only"
        name={`${name}-selling-plan`}
        value={isActive}
        onChange={() => onValueChange(selling_plan.id)}
        aria-label="selling plan"
      />
      <p className="body--regular text-center first-letter:capitalize">
        {translateRecharge(selling_plan.name)}
      </p>
      {!hideDynamicLabel && (
        <p className="dynamic-label text-center text-xs font-normal italic leading-[130%] opacity-50">
          {t("product_card.subscription_tag", {
            percentage: selling_plan.price_adjustments[0].value,
          })}
        </p>
      )}
    </label>
  );
};

SellingPlanRadio.propTypes = {
  group: PropTypes.object,
  selling_plan: PropTypes.object.isRequired,
  price: PropTypes.number,
  hideDynamicLabel: PropTypes.bool,
  name: PropTypes.string.isRequired,
  tag: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  onValueChange: PropTypes.func,
};

const QuantityRadio = ({
  isActive,
  onValueChange,
  name,
  label,
  tag,
  discountPercentage,
}) => {
  const {t} = useTranslation();
  return (
    <label
      className={cn(
        "relative flex flex-[1_0_0%] cursor-pointer flex-col items-center justify-center rounded-[6.25rem] border px-6 py-4 transition-all xl:flex-[1_0_0%]",
        isActive
          ? "button--quaternary border-deep-green bg-deep-green hover:bg-deep-green hover:text-white"
          : "button--secondary border-deep-green hover:bg-deep-green lg:hover:opacity-70",
      )}
    >
      <Tag label={tag} />
      <input
        type="radio"
        name={name}
        className="sr-only"
        checked={isActive}
        onChange={onValueChange}
        aria-label={name}
      />
      <p className="body--regular whitespace-nowrap">{label}</p>
      <p className="dynamic-label text-center text-xs font-normal italic leading-[130%] opacity-50">
        {!!discountPercentage &&
          t("product_card.subscription_tag", {
            percentage: discountPercentage,
          })}
      </p>
    </label>
  );
};

QuantityRadio.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  onValueChange: PropTypes.func,
  tag: PropTypes.string,
  discountPercentage: PropTypes.number,
};

const Tag = ({label}) =>
  label ? (
    <span className="tag tag--secondary absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap bg-light-yellow px-2 py-1 font-dm-sans text-[0.825rem] md:py-[0.12rem] md:font-semibold md:leading-[130%]">
      {label}
    </span>
  ) : (
    <></>
  );

const getUnitypeLabel = (qty, measure, t) => {
  const customLabel = measure?.split(',');
  if(window.Shopify.locale === 'de' && typeof customLabel === 'object' && customLabel.length === 2) {
    return qty === 1 ? `1 ${customLabel[0]}` : `${qty} ${customLabel[1]}`;
  } else {
    return t("product.bottle", {count: qty});
  }
}

Tag.propTypes = {
  label: PropTypes.string,
};

export default ProductForm;